import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Send, ShieldCheck, Truck, RotateCcw, Clock } from 'lucide-react';
import { message } from 'antd';
import { apiService } from '../../../services/api';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const [newsletterInput, setNewsletterInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterInput.trim()) {
      message.warning('Vui lòng nhập email hoặc số điện thoại');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiService.post('/api/contact/newsletter', {
        contactInfo: newsletterInput.trim()
      });
      if (response.data?.success) {
        message.success(response.data.message || 'Đăng ký nhận ưu đãi thành công!');
        setNewsletterInput('');
      } else {
        message.error(response.data?.message || 'Đăng ký không thành công. Vui lòng thử lại sau.');
      }
    } catch (error) {
      console.error('Newsletter submission error:', error);
      message.error(error.response?.data?.message || 'Có lỗi xảy ra trong quá trình đăng ký. Vui lòng thử lại sau.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hideFooter = location.pathname === '/login' || location.pathname === '/register';
  if (hideFooter) return null;

  return (
    <footer className="relative bg-[#0B0F19] text-slate-400 pt-10 pb-6 overflow-hidden border-t border-white/5">
      {/* Subtle Glows */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-amber-500/5 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Compact Trust Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-10 border-b border-white/5 mb-10">
          {[
            { icon: <ShieldCheck className="w-5 h-5" />, title: "Chính Hãng", desc: "Cam kết 100%" },
            { icon: <Truck className="w-5 h-5" />, title: "Giao Toàn Quốc", desc: "Nhanh chóng" },
            { icon: <RotateCcw className="w-5 h-5" />, title: "Đổi Trả", desc: "Linh hoạt" },
            { icon: <Clock className="w-5 h-5" />, title: "Hỗ Trợ 24/7", desc: "Chuyên nghiệp" }
          ].map((item, idx) => (
            <div key={idx} className="flex items-center justify-start pl-4 sm:pl-12 md:pl-0 gap-3 group">
              <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center text-amber-500 group-hover:bg-amber-500/10 transition-colors shrink-0">
                {item.icon}
              </div>
              <div className="text-left">
                <h4 className="text-white font-bold text-xs">{item.title}</h4>
                <p className="text-[10px] text-slate-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Column 1: Brand & Social */}
          <div className="space-y-4 flex flex-col items-center md:items-start text-center md:text-left">
            <Link to="/" className="inline-block">
              <div className="flex items-center gap-3 justify-center md:justify-start">
                <img 
                  src="/images/den_hai_dang_lao_cai_den-hai-dang-logo.webp" 
                  alt="Logo" 
                  className="h-10 w-auto rounded-md shadow-lg"
                />
                <h2 className="text-lg font-black text-white tracking-tighter">ĐÈN HẢI ĐĂNG</h2>
              </div>
            </Link>
            <p className="text-xs leading-relaxed text-slate-500 max-w-xs mx-auto md:mx-0">
              Chuyên cung cấp giải pháp chiếu sáng nghệ thuật và đá trang trí cao cấp tại Lào Cai. 10+ năm kinh nghiệm.
            </p>
            <div className="flex gap-4 pt-2 justify-center md:justify-start">
              <a 
                href="https://facebook.com/haidanggolc" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 rounded-full flex items-center justify-center bg-[#1877F2] text-white hover:bg-[#166FE5] transition-all duration-300 shadow-lg hover:scale-105 active:scale-95"
                title="Facebook"
              >
                <Facebook className="w-5 h-5 fill-current" />
              </a>
              <a 
                href="https://zalo.me/0983018234" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 rounded-full flex items-center justify-center bg-[#0068FF] text-white hover:bg-[#005AE6] transition-all duration-300 shadow-lg hover:scale-105 active:scale-95"
                title="Zalo"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.49 10.2722v-.4496h1.3467v6.3218h-.7704a.576.576 0 01-.5763-.5729l-.0006.0005a3.273 3.273 0 01-1.9372.6321c-1.8138 0-3.2844-1.4697-3.2844-3.2823 0-1.8125 1.4706-3.2822 3.2844-3.2822a3.273 3.273 0 011.9372.6321l.0006.0005zM6.9188 7.7896v.205c0 .3823-.051.6944-.2995 1.0605l-.03.0343c-.0542.0615-.1815.206-.2421.2843L2.024 14.8h4.8948v.7682a.5764.5764 0 01-.5767.5761H0v-.3622c0-.4436.1102-.6414.2495-.8476L4.8582 9.23H.1922V7.7896h6.7266zm8.5513 8.3548a.4805.4805 0 01-.4803-.4798v-7.875h1.4416v8.3548H15.47zM20.6934 9.6C22.52 9.6 24 11.0807 24 12.9044c0 1.8252-1.4801 3.306-3.3066 3.306-1.8264 0-3.3066-1.4808-3.3066-3.306 0-1.8237 1.4802-3.3044 3.3066-3.3044zm-10.1412 5.253c1.0675 0 1.9324-.8645 1.9324-1.9312 0-1.065-.865-1.9295-1.9324-1.9295s-1.9324.8644-1.9324 1.9295c0 1.0667.865 1.9312 1.9324 1.9312zm10.1412-.0033c1.0737 0 1.945-.8707 1.945-1.9453 0-1.073-.8713-1.9436-1.945-1.9436-1.0753 0-1.945.8706-1.945 1.9436 0 1.0746.8697 1.9453 1.945 1.9453z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="text-center md:text-left">
            <h3 className="text-white font-semibold text-sm mb-4">Khám phá</h3>
            <ul className="space-y-2.5">
              <li><Link to="/products?category=67794356c39f02c6f6004b99" className="hover:text-amber-500 text-xs transition-colors">Đèn trang trí</Link></li>
              <li><Link to="/products?category=67b69335f661df280d96f4a8" className="hover:text-amber-500 text-xs transition-colors">Đá trang trí</Link></li>
              <li><Link to="/about" className="hover:text-amber-500 text-xs transition-colors">Về chúng tôi</Link></li>
            </ul>
          </div>

          {/* Column 3: Contact & Address */}
          <div className="text-center md:text-left">
            <h3 className="text-white font-semibold text-sm mb-4">Liên hệ</h3>
            <ul className="space-y-3 flex flex-col items-center md:items-start">
              <li className="text-xs">
                <a href="tel:0983018234" className="hover:text-white font-bold flex items-center justify-center md:justify-start gap-2">
                  <Phone className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <span>0983 018 234</span>
                </a>
              </li>
              <li className="text-xs">
                <a href="mailto:haidanggolc@gmail.com" className="hover:text-white flex items-center justify-center md:justify-start gap-2">
                  <Mail className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <span className="truncate">haidanggolc@gmail.com</span>
                </a>
              </li>
              <li className="flex items-start gap-2 text-xs justify-center md:justify-start text-center md:text-left">
                <MapPin className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                <span className="text-slate-400 italic">315 Trần Phú, P. Cam Đường, TP. Lào Cai</span>
              </li>
            </ul>
          </div>

          {/* Column 4: Newsletter */}
          <div className="space-y-4 flex flex-col items-center md:items-start text-center md:text-left">
            <h3 className="text-white font-semibold text-sm">Nhận ưu đãi</h3>
            <p className="text-xs text-slate-500 leading-relaxed max-w-xs mx-auto md:mx-0">
              Đăng ký để nhận thông tin khuyến mãi và sản phẩm mới nhất từ chúng tôi.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex items-center bg-white/5 border border-white/10 rounded-lg max-w-xs h-10 overflow-hidden w-full mx-auto md:mx-0">
              <input 
                type="text" 
                placeholder="Nhập email hoặc SĐT..." 
                value={newsletterInput}
                onChange={(e) => setNewsletterInput(e.target.value)}
                disabled={isSubmitting}
                className="bg-transparent border-none text-xs text-white px-3 h-full w-full focus:ring-0 focus:outline-none"
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                className="bg-amber-600 hover:bg-amber-700 text-white h-full px-4 transition-colors flex items-center justify-center disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-6 pt-4 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4 text-[10px] text-slate-600">
          <p>&copy; {currentYear} <span className="text-slate-400 font-bold">Đèn Hải Đăng Lào Cai</span>. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 bg-white/5 rounded-full border border-white/10">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-slate-500 font-medium">Online</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
